import { getProvider } from '../svpProvider';
import { getLogger } from '../logger';
import { omit } from '../../helpers/types';

import { isSuccessResponse, requestAPI } from './shared/requestAPI';

import type {
  PageResponse,
  PageListItem,
  PageInput,
  ComponentResponse,
} from '../../types';

const logger = getLogger('pagesApi');

export const cleanupPageInput = (
  data: Partial<PageInput>
): Partial<PageInput> => {
  const cleaned = omit(data, [
    '__v',
    'provider',
    'updatedAt',
    'createdAt',
    'updatedBy',
  ] as const);
  const blob = cleaned as Record<string, unknown>;
  if (!blob.entityId) {
    if (blob.category) {
      blob.entityId = blob.category;
      delete blob.category;
    }
    if (blob.tag) {
      blob.entityId = blob.tag;
      delete blob.tag;
    }
  }
  return cleaned;
};

export const cleanupExpandedPage = (page: PageResponse) => {
  const cleaned = omit(page, ['components', 'tag', 'category'] as const);
  return cleanupPageInput({
    ...cleaned,
    components: page.components?.map(({ id }) => id),
  });
};

export async function fetchPages() {
  try {
    const result = await requestAPI<PageListItem[]>(`/${getProvider()}/pages`);
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to fetch the list of pages');
    throw error;
  }
}

export async function fetchPage(pageId: PageResponse['id']) {
  try {
    const result = await requestAPI<PageResponse>(
      `/${getProvider()}/pages/${pageId}`
    );
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to fetch page', { pageId });
    throw error;
  }
}

export async function createPage(data: Partial<PageInput>) {
  const fields = cleanupPageInput(data);
  try {
    const result = await requestAPI<PageResponse>(`/${getProvider()}/pages`, {
      method: 'POST',
      body: JSON.stringify(fields),
      headers: { 'Content-Type': 'application/json' },
    });
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to create page', { fields });
    throw error;
  }
}

export async function updatePage(
  pageId: PageResponse['id'],
  data: Partial<PageInput>
) {
  const fields = cleanupPageInput(data);
  try {
    const result = await requestAPI<PageResponse>(
      `/${getProvider()}/pages/${pageId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(fields),
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to update page', { pageId, fields });
    throw error;
  }
}

export async function removePageComponent(
  pageId: PageResponse['id'],
  componentId: ComponentResponse['id']
) {
  try {
    const result = await requestAPI<PageResponse>(
      `/${getProvider()}/pages/${pageId}/components/${componentId}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to remove page component', {
      error,
      pageId,
      componentId,
    });
    throw error;
  }
}

export async function addPageComponent(
  pageId: PageResponse['id'],
  componentId: ComponentResponse['id'],
  index: number
) {
  try {
    const result = await requestAPI<PageResponse>(
      `/${getProvider()}/pages/${pageId}/components/${componentId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ index }),
      }
    );
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to add page component', {
      pageId,
      componentId,
    });
    throw error;
  }
}
